import { z } from "zod";

// ============================================================================
// PROFILE SCHEMAS (camelCase for API)
// ============================================================================

// System roles
export const SystemRoleSchema = z.enum(["super-admin", "admin", "beta-user", "user"]);

// Base profile schema
// Note: email, firstName, lastName can be null for incomplete profiles
export const ProfileSchema = z.object({
  id: z.string(),
  userId: z.string(),
  historyVersion: z.number(),
  createdAt: z.string(),
  updatedAt: z.string(),
  email: z.string().nullable(),
  firstName: z.string().nullable(),
  lastName: z.string().nullable(),
  jobTitle: z.string().nullable(),
  company: z.string().nullable(),
  bio: z.string().nullable(),
  phone: z.string().nullable(),
  website: z.string().nullable(),
  address: z.string().nullable(),
  avatarPublicUrl: z.string().nullable(),
});

// Profile with role (for admin endpoints)
export const ProfileWithRoleSchema = ProfileSchema.extend({
  id: z.string().nullable(),
  role: SystemRoleSchema,
});

export const ProfileWithRoleListDbSchema = z.array(ProfileWithRoleSchema);

// ============================================================================
// CREATE/UPDATE PAYLOAD SCHEMAS
// ============================================================================

export const CreateProfileBodySchema = z.object({
  firstName: z.string().min(1, "First name is required"),
  lastName: z.string().min(1, "Last name is required"),
  jobTitle: z.string().optional(),
  company: z.string().optional(),
  bio: z.string().optional(),
  phone: z.string().optional(),
  website: z.string().optional(),
  address: z.string().optional(),
  avatarPublicUrl: z.string().optional(),
});

export const UpdateProfileBodySchema = CreateProfileBodySchema.partial();

// Set user role (admin only)
export const SetUserRoleBodySchema = z.object({
  userId: z.string().min(1, "User ID is required"),
  role: SystemRoleSchema,
});

// ============================================================================
// TYPE EXPORTS
// ============================================================================

export type SystemRole = z.infer<typeof SystemRoleSchema>;
export type Profile = z.infer<typeof ProfileSchema>;
export type ProfileWithRole = z.infer<typeof ProfileWithRoleSchema>;
export type ProfileWithRoleList = z.infer<typeof ProfileWithRoleListDbSchema>;
export type CreateProfileBody = z.infer<typeof CreateProfileBodySchema>;
export type UpdateProfileBody = z.infer<typeof UpdateProfileBodySchema>;
export type SetUserRoleBody = z.infer<typeof SetUserRoleBodySchema>;

// API Response types
export type CreateProfileApiResponse = Profile;
export type UpdateProfileApiResponse = Profile;
